import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ImagePlus, ListPlus, Sparkles } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

interface PlanStatus { running: boolean; total: number; done: number; failed: number; last_error: string; planned: number; planned_without_draft: number; drafts_ready: number }

export function ContentPlanBar({ onChange }: { onChange: () => void }) {
  const [st, setSt] = useState<PlanStatus | null>(null);
  const load = useCallback(() => api.get("/admin/posts/plan/status").then((r) => setSt(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!st?.running) return;
    const t = setInterval(() => { load(); onChange(); }, 8000);
    return () => clearInterval(t);
  }, [st?.running, load, onChange]);

  const loadPlan = async () => {
    try { const { data } = await api.post("/admin/posts/plan/load"); toast.success(`Added ${data.created} new planned posts`); load(); onChange(); } catch (e) { toast.error(formatApiError(e)); }
  };
  const draftAll = async () => {
    try {
      const { data } = await api.post("/admin/posts/plan/draft-all");
      if (data.accepted) toast.success(`Writing ${data.total} drafts. This takes a few minutes; you can leave this page.`); else toast("Every planned post already has a draft");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="mb-6 flex flex-wrap items-center gap-3 rounded-2xl border-2 border-dashed border-[#23324A]/30 bg-[#FDFBF7] p-4" data-testid="content-plan-bar">
      <div className="min-w-0 flex-1 text-sm text-[#23324A]">
        <p className="font-bold">New content plan</p>
        <p className="text-[#23324A]/70" data-testid="plan-status-text">
          {!st ? "Loading…" : st.running ? `Writing drafts: ${st.done + st.failed} of ${st.total} done${st.failed ? ` (${st.failed} failed)` : ""}`
            : `${st.planned} planned (hidden) · ${st.drafts_ready} drafts waiting for your approval`}
          {st?.last_error && !st.running ? ` · Last error: ${st.last_error}` : ""}
        </p>
      </div>
      <button className="btn-outline" onClick={loadPlan} data-testid="plan-load-btn"><ListPlus className="h-4 w-4" strokeWidth={2.5} />Add planned posts</button>
      <button className="btn-coral" disabled={!st || st.running || st.planned_without_draft === 0} onClick={draftAll} data-testid="plan-draft-all-btn">
        <Sparkles className="h-4 w-4" strokeWidth={2.5} />{st?.running ? "Writing…" : "Write all drafts"}
      </button>
    </div>
  );
}

export function PostImageButton({ slug, onDone }: { slug: string; onDone: () => void }) {
  const ref = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const upload = async (file?: File) => {
    if (!file) return;
    setBusy(true);
    const form = new FormData();
    form.append("file", file);
    try { await api.post(`/admin/posts/${slug}/image`, form, { timeout: 120000 }); toast.success("Photo added"); onDone(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); if (ref.current) ref.current.value = ""; }
  };
  return (
    <>
      <input ref={ref} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid={`post-image-input-${slug}`} />
      <button className="btn-ghost" disabled={busy} onClick={() => ref.current?.click()} aria-label="Add photo" data-testid={`post-image-btn-${slug}`}>
        <ImagePlus className="h-4 w-4" strokeWidth={2.5} />{busy ? "…" : ""}
      </button>
    </>
  );
}
